import { buildLevel, canvasMeasure } from '../level/layout';
import { F, type Level, type Occurrence, type Tile } from '../level/types';
import {
  fetchPage,
  fetchTargetGraph,
  getLang,
  normalizeTitle,
  resolveRedirects,
  resolveTitle,
  setLang,
  type TargetGraph,
} from '../wiki/api';
import { extractArticle } from '../wiki/extract';
import { Enemy, Particles } from './actors';
import { Audio } from './audio';
import { Input, type Action } from './input';
import { Player, type PlayerEvent } from './player';
import { MiniMap, Renderer, type Indicator, type Scene } from './render';
import { World } from './world';

export type Mode = 'daily' | 'random' | 'custom' | 'explore';

export interface RaceConfig {
  mode: Mode;
  start: string;
  target?: string;
  daily?: number;
  /** Wikipedia language edition (default "en"). */
  lang?: string;
}

export interface RaceResult {
  cfg: RaceConfig;
  target: string;
  hops: number;
  timeMs: number;
  deaths: number;
  sources: number;
  insights: number;
  path: string[];
  shortcut?: string[];
}

export interface GameHooks {
  hud(): void;
  clock(ms: number): void;
  level(level: Level): void;
  standing(title: string | null, exit: boolean): void;
  toast(msg: string, kind?: 'info' | 'good' | 'bad'): void;
  loading(title: string | null): void;
  win(r: RaceResult): void;
  pause(): void;
  /** A gamepad asked to leave the current overlay (pause menu, find box) and keep playing. */
  resume(): void;
  error(msg: string, retry: () => void): void;
  find(): void;
}

type State = 'idle' | 'play' | 'dive' | 'travel' | 'arrive' | 'won' | 'paused' | 'peek';

/** States where game keys are captured; elsewhere (menus, results) the keyboard drives the DOM. */
const CAPTURE = new Set<State>(['play', 'dive', 'travel', 'arrive', 'peek']);

export const INSIGHT_COST = 5;
const STEP = 1 / 120;
const LINK_STATE = F.EXIT | F.VISITED | F.HOT | F.WARM;

export class Game {
  readonly renderer: Renderer;
  readonly minimap: MiniMap;
  readonly input: Input;
  readonly audio = new Audio();
  state: State = 'idle';
  cfg: RaceConfig | null = null;
  level: Level | null = null;
  world: World | null = null;
  player: Player | null = null;
  enemies: Enemy[] = [];
  coinsTaken = new Uint8Array(0);
  particles = new Particles();
  time = 0;
  hops = 0;
  timeMs = 0;
  deaths = 0;
  sources = INSIGHT_COST;
  insights = 0;
  path: string[] = [];
  stack: string[] = [];
  visited = new Set<string>();
  checkpoint = 0;
  target: { title: string; aliases: Set<string>; graph: Promise<TargetGraph> | null } | null = null;
  levelInsight = false;
  reducedMotion = false;
  findOcc: number | null = null;

  private startLinks: Set<string> | null = null;
  private actionTile: Tile | null = null;
  private standingTitle: string | null = null;
  private standTime = 0;
  private dive = 0;
  private wipe = 0;
  private hurtFlash = 0;
  private winPending = false;
  private wonT = 0;
  private peekT = 0;
  private peek: { x: number; y: number } | null = null;
  private exitOccs: Occurrence[] = [];
  private hotOccs: Occurrence[] = [];
  private raceId = 0;
  private waiters: { cond: () => boolean; resolve: () => void }[] = [];
  private last = 0;
  private acc = 0;
  private lastClock = -1;

  constructor(
    canvas: HTMLCanvasElement,
    minimap: HTMLCanvasElement,
    private hooks: GameHooks,
  ) {
    this.renderer = new Renderer(canvas);
    this.minimap = new MiniMap(minimap);
    this.input = new Input(() => CAPTURE.has(this.state));
    window.addEventListener('resize', () => this.resize());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
    });
  }

  resize(): void {
    this.renderer.resize();
    this.minimap.rebuild();
  }

  run(): void {
    const loop = (t: number) => {
      this.frame(t);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  // ---------------------------------------------------------------- races

  async startRace(cfg: RaceConfig): Promise<void> {
    const raceId = ++this.raceId;
    setLang(cfg.lang ?? 'en');
    this.cfg = cfg;
    this.state = 'idle';
    this.hops = 0;
    this.timeMs = 0;
    this.deaths = 0;
    this.sources = INSIGHT_COST;
    this.insights = 0;
    this.path = [];
    this.stack = [];
    this.visited.clear();
    this.target = null;
    this.winPending = false;
    this.lastClock = -1;
    this.hooks.loading(cfg.start);
    try {
      const [targetTitle, level] = await Promise.all([
        cfg.target ? resolveTitle(cfg.target) : Promise.resolve(undefined),
        this.loadLevel(cfg.start, false),
      ]);
      if (raceId !== this.raceId) return;
      if (cfg.target) {
        if (!targetTitle) throw new Error(`The target “${cfg.target}” doesn't exist on Wikipedia.`);
        if (normalizeTitle(targetTitle) === normalizeTitle(level.title))
          throw new Error('Start and target are the same article. Pick two different ones!');
        this.target = { title: targetTitle, aliases: new Set([targetTitle]), graph: null };
        this.targetGraph().catch(() => undefined);
      }
      this.startLinks = new Set(level.links.keys());
      this.path = [level.title];
      this.stack = [level.title];
      this.visited.add(level.title);
      this.install(level);
      this.hooks.loading(null);
      this.wipe = 1;
      this.state = 'arrive';
      this.audio.play('arrive');
    } catch (e) {
      if (raceId !== this.raceId) return;
      this.hooks.loading(null);
      this.state = 'idle';
      this.hooks.error((e as Error).message || 'Something went wrong.', () => void this.startRace(cfg));
    }
  }

  quit(): void {
    this.raceId++;
    this.state = 'idle';
    this.level = null;
    this.world = null;
    this.player = null;
    this.input.clear();
  }

  pause(): void {
    if (this.state === 'play' || this.state === 'peek') {
      this.state = 'paused';
      this.input.clear();
      this.hooks.pause();
    }
  }

  resume(): void {
    if (this.state === 'paused') {
      this.state = 'play';
      this.last = performance.now();
      // The press that closed the menu must not also jump or dive.
      this.input.dropPresses();
    }
  }

  /** Freeze play without opening the pause menu (e.g. while the find box is open). */
  hold(): void {
    if (this.state === 'play') {
      this.state = 'paused';
      this.input.clear();
    }
  }

  get canGoBack(): boolean {
    return this.stack.length >= 2;
  }

  goBack(): void {
    if ((this.state !== 'play' && this.state !== 'paused') || this.stack.length < 2) return;
    this.state = 'dive';
    this.dive = 0;
    this.audio.play('dive');
    void this.travel(this.stack[this.stack.length - 2], 'back');
  }

  isTarget(title: string): boolean {
    if (!this.target) return false;
    return this.target.aliases.has(title) || normalizeTitle(title) === normalizeTitle(this.target.title);
  }

  /** The target's link neighbourhood: fetched once per race, and fetched again if an earlier attempt failed. */
  private targetGraph(): Promise<TargetGraph> {
    const target = this.target;
    if (!target) return Promise.reject(new Error('This race has no target.'));
    if (!target.graph) {
      const p = fetchTargetGraph(target.title);
      target.graph = p;
      p.then(
        (g) => {
          if (this.target !== target) return;
          for (const a of g.aliases) target.aliases.add(a);
          this.applyLinkFlags();
        },
        () => {
          if (target.graph === p) target.graph = null;
        },
      );
    }
    return target.graph;
  }

  private async loadLevel(title: string, hasBack: boolean): Promise<Level> {
    const page = await fetchPage(title);
    const doc = extractArticle(page.html, {
      title: page.title,
      displayTitle: page.displayTitle,
      description: page.description,
      pageId: page.pageId,
      revId: page.revId,
      lang: getLang(),
    });
    return buildLevel(doc, { measure: canvasMeasure, hasBack });
  }

  private until(cond: () => boolean): Promise<void> {
    if (cond()) return Promise.resolve();
    return new Promise((resolve) => this.waiters.push({ cond, resolve }));
  }

  private async travel(title: string, kind: 'link' | 'back'): Promise<void> {
    const raceId = this.raceId;
    const hasBack = kind === 'link' ? true : this.stack.length - 1 >= 2;
    const load = this.loadLevel(title, hasBack);
    load.catch(() => undefined);
    await this.until(() => this.wipe >= 1 || raceId !== this.raceId);
    if (raceId !== this.raceId) return;
    const slow = setTimeout(() => this.hooks.loading(title), 250);
    let level: Level;
    try {
      level = await load;
    } catch (e) {
      clearTimeout(slow);
      if (raceId !== this.raceId) return;
      this.hooks.loading(null);
      this.hooks.toast(`Couldn't open “${title}”. ${(e as Error).message}`, 'bad');
      this.dive = 0;
      this.state = 'arrive';
      return;
    }
    clearTimeout(slow);
    if (raceId !== this.raceId) return;
    this.hooks.loading(null);
    this.hops++;
    if (kind === 'back') this.stack.pop();
    else this.stack.push(level.title);
    this.path.push(level.title);
    this.visited.add(level.title);
    this.visited.add(title);
    this.install(level);
    this.dive = 0;
    this.state = 'arrive';
    this.audio.play('arrive');
    if (this.isTarget(level.title)) this.winPending = true;
  }

  private install(level: Level): void {
    this.level = level;
    this.world = new World(level);
    this.levelInsight = false;
    this.findOcc = null;
    this.applyLinkFlags();
    this.player = new Player(level.spawn.x, level.spawn.y);
    this.enemies = level.enemies.map((e, i) => new Enemy(e.x, e.y, e.kind, i % 2 ? 1 : -1));
    this.coinsTaken = new Uint8Array(level.coins.length);
    this.checkpoint = 0;
    this.actionTile = null;
    this.standingTitle = null;
    this.particles.list.length = 0;
    const [tx, ty] = this.renderer.cam.targetFor(level, this.player.cx, this.player.y, 0);
    this.renderer.cam.follow(tx, ty, 0, true);
    this.minimap.setLevel(level);
    this.hooks.standing(null, false);
    this.hooks.level(level);
    this.hooks.hud();
  }

  /** Mirror exit/visited/insight state from link infos onto their tiles. */
  private applyLinkFlags(): void {
    const level = this.level;
    if (!level) return;
    this.exitOccs = [];
    this.hotOccs = [];
    for (const info of level.links.values()) {
      info.flags &= F.HOT | F.WARM | F.EXIT;
      if (this.isTarget(info.title)) info.flags |= F.EXIT;
      if (this.visited.has(info.title)) info.flags |= F.VISITED;
      for (const id of info.occs) {
        const o = level.occs[id];
        if (!o) continue;
        if (info.flags & F.EXIT) this.exitOccs.push(o);
        else if (info.flags & F.HOT) this.hotOccs.push(o);
        for (const tid of o.tiles) {
          const t = level.tiles[tid];
          t.flags = (t.flags & ~LINK_STATE) | info.flags;
        }
      }
    }
    this.minimap.setLevel(level);
  }

  // ---------------------------------------------------------------- abilities

  async useInsight(): Promise<void> {
    const level = this.level;
    if (this.state !== 'play' || !level) return;
    if (!this.target) {
      this.hooks.toast('Insight points toward a target, and free explore has none.');
      return;
    }
    if (this.levelInsight) {
      this.hooks.toast('Insight is already active on this page.');
      return;
    }
    if (this.sources < INSIGHT_COST) {
      this.hooks.toast(`Insight costs ${INSIGHT_COST} sources. Grab the gold [citations] floating on the page.`, 'bad');
      return;
    }
    const target = this.target;
    const raceId = this.raceId;
    this.sources -= INSIGHT_COST;
    this.insights++;
    this.levelInsight = true;
    this.audio.play('insight');
    this.hooks.hud();
    this.hooks.toast('🧭 Consulting the sources…');
    const refund = (msg: string, kind: 'info' | 'bad') => {
      if (raceId !== this.raceId) return;
      this.sources += INSIGHT_COST;
      this.insights--;
      if (this.level === level) this.levelInsight = false;
      this.hooks.hud();
      this.hooks.toast(msg, kind);
    };
    try {
      const g = await this.targetGraph();
      const redirects = [...level.links.values()].filter((l) => l.redirect).map((l) => l.title);
      const canon = await resolveRedirects(redirects);
      if (raceId !== this.raceId) return;
      if (this.level !== level) return refund('🧭 You left the page before Insight finished. Sources refunded.', 'info');
      let hot = 0;
      let warm = 0;
      for (const info of level.links.values()) {
        const c = canon.get(info.title) ?? info.title;
        info.flags &= ~(F.HOT | F.WARM);
        if (g.aliases.has(c) || this.isTarget(c)) info.flags |= F.EXIT;
        else if (g.backlinks.has(c) || g.backlinks.has(info.title)) {
          info.flags |= F.HOT;
          hot++;
        } else if (g.forward.has(c) || g.forward.has(info.title)) {
          info.flags |= F.WARM;
          warm++;
        }
      }
      this.applyLinkFlags();
      if (this.exitOccs.length) this.hooks.toast(`🧭 The way to “${target.title}” is right here. Follow the gold!`, 'good');
      else if (hot)
        this.hooks.toast(`🧭 ${hot} hot link${hot === 1 ? '' : 's'} (one hop from “${target.title}”) and ${warm} warm.`, 'good');
      else if (warm) this.hooks.toast(`🧭 No direct routes here, but ${warm} warm lead${warm === 1 ? '' : 's'}.`, 'good');
      else this.hooks.toast('🧭 No strong leads on this page. Try a broader topic.', 'info');
    } catch {
      refund('Insight could not reach Wikipedia. Your sources were refunded.', 'bad');
    }
  }

  findMatches(q: string): Occurrence[] {
    const level = this.level;
    const s = q.trim().toLowerCase();
    if (!level || !s) return [];
    const out: Occurrence[] = [];
    const seen = new Set<string>();
    for (const o of level.occs) {
      if (!(o.text.toLowerCase().includes(s) || o.title.toLowerCase().includes(s))) continue;
      const key = `${o.title}|${o.section}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(o);
      if (out.length >= 80) break;
    }
    return out;
  }

  sectionTitle(i: number): string {
    return this.level?.sections[i]?.title ?? '';
  }

  linkState(title: string): number {
    return this.level?.links.get(title)?.flags ?? 0;
  }

  focusOcc(id: number | null): void {
    const level = this.level;
    if (!level) return;
    if (this.findOcc !== null)
      for (const tid of level.occs[this.findOcc]?.tiles ?? []) level.tiles[tid].flags &= ~F.FOUND;
    this.findOcc = id;
    if (id === null) return;
    const o = level.occs[id];
    if (!o) return;
    for (const tid of o.tiles) level.tiles[tid].flags |= F.FOUND;
    this.peek = { x: o.x, y: o.y };
    this.peekT = 1.7;
    if (this.state === 'play' || this.state === 'paused') this.state = 'peek';
  }

  // ---------------------------------------------------------------- loop

  private frame(now: number): void {
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    this.input.poll();
    this.globalInput();
    let stepped = false;
    if (this.level && this.world && this.player) {
      this.time += dt;
      this.acc += dt;
      let n = 0;
      while (this.acc >= STEP && n < 8) {
        this.update(STEP, n === 0);
        this.acc -= STEP;
        n++;
      }
      if (n === 8) this.acc = 0;
      stepped = n > 0;
      this.camera(dt);
      this.renderer.draw(this.scene());
      const p = this.player;
      this.minimap.draw(this.renderer.cam, p.cx, p.y, this.mapMarks(), this.time);
      const sec = Math.floor(this.timeMs / 1000);
      if (sec !== this.lastClock) {
        this.lastClock = sec;
        this.hooks.clock(this.timeMs);
      }
    }
    for (let i = this.waiters.length - 1; i >= 0; i--)
      if (this.waiters[i].cond()) {
        this.waiters[i].resolve();
        this.waiters.splice(i, 1);
      }
    // Gameplay presses survive frames that ran no simulation step (common above 60 Hz).
    if (stepped || !this.level) this.input.consumeStep();
    this.input.endFrame();
  }

  private globalInput(): void {
    const p = this.input.pressed;
    if (this.state === 'paused') {
      // Only gamepads reach here while paused (keys aren't captured): Menu, B or View backs out.
      if (p.has('pause') || p.has('dive') || p.has('find')) this.hooks.resume();
      return;
    }
    if (p.has('pause')) this.pause();
    if (this.state !== 'play') return;
    if (p.has('find')) this.hooks.find();
    if (p.has('insight')) void this.useInsight();
    if (p.has('respawn')) this.respawn();
  }

  private update(dt: number, first: boolean): void {
    const world = this.world!;
    this.particles.step(dt);
    this.hurtFlash = Math.max(0, this.hurtFlash - dt * 1.5);
    switch (this.state) {
      case 'play':
        world.time += dt;
        this.timeMs += dt * 1000;
        this.play(dt, first);
        break;
      case 'dive': {
        world.time += dt;
        const p = this.player!;
        p.vx = 0;
        this.dive = Math.min(1, this.dive + dt / 0.42);
        if (this.dive >= 1) this.state = 'travel';
        break;
      }
      case 'travel':
        this.wipe = Math.min(1, this.wipe + dt / 0.3);
        break;
      case 'arrive':
        world.time += dt;
        this.wipe = Math.max(0, this.wipe - dt / 0.45);
        this.player!.step(dt, NO_INPUT, world, (e, t) => this.onPlayerEvent(e, t));
        if (this.wipe <= 0) {
          if (this.winPending) this.win();
          else this.state = 'play';
        }
        break;
      case 'won':
        world.time += dt;
        this.wonT += dt;
        this.player!.step(dt, NO_INPUT, world, () => undefined);
        if (this.wonT < 1.2 && Math.random() < 0.5) this.confetti();
        if (this.wonT >= 1.5 && this.wonT - dt < 1.5) void this.finish();
        break;
      case 'peek':
        this.peekT -= dt;
        if (this.peekT <= 0 || (first && this.input.stepPressed.size)) this.state = 'play';
        break;
      default:
        break;
    }
  }

  private play(dt: number, first: boolean): void {
    const inp = this.input;
    const p = this.player!;
    const world = this.world!;
    const level = this.level!;
    const onCol = p.onLadder(world);
    const pressed = (a: Action) => first && inp.stepPressed.has(a);
    const down = pressed('down');
    if ((down || pressed('dive')) && p.onGround) {
      const t = p.actionTile();
      if (t) {
        this.activate(t);
        return;
      }
      const g = p.ground;
      if (down && g && g.flags & F.ONEWAY && !onCol) p.dropTimer = 0.22;
    }
    p.step(
      dt,
      {
        left: inp.held.has('left'),
        right: inp.held.has('right'),
        up: inp.held.has('up'),
        down: inp.held.has('down'),
        jump: inp.held.has('jump') || (inp.held.has('up') && !onCol),
        jumpPressed: pressed('jump') || (pressed('up') && !onCol),
      },
      world,
      (e, t) => this.onPlayerEvent(e, t),
    );

    const act = p.onGround ? p.actionTile() : null;
    this.actionTile = act;
    const title = act && act.flags & F.LINK ? (act.link ?? null) : null;
    if (title !== this.standingTitle) {
      this.standingTitle = title;
      this.standTime = 0;
      this.hooks.standing(title, !!act && (act.flags & F.EXIT) !== 0);
    } else if (title) {
      const before = this.standTime;
      this.standTime += dt;
      if (before < 0.3 && this.standTime >= 0.3) fetchPage(title).catch(() => undefined);
    }

    if (p.onGround) this.checkpoint = this.sectionAt(p.y + p.h);
    this.coins();
    this.updateEnemies(dt);
    if (p.y > level.height + 100) this.hurt();
  }

  private sectionAt(y: number): number {
    const s = this.level!.sections;
    let lo = 0;
    let hi = s.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (s[mid].y <= y) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  private activate(t: Tile): void {
    const level = this.level!;
    const p = this.player!;
    if (t.door) {
      let dest: { x: number; y: number } | undefined;
      switch (t.door.kind) {
        case 'toc':
          dest = level.sections[t.door.section]?.spawn;
          break;
        case 'contents':
          dest = level.tocSpawn ?? level.spawn;
          break;
        case 'top':
          dest = level.spawn;
          break;
        case 'back':
          this.goBack();
          return;
      }
      if (!dest) return;
      this.burst(p.cx, p.y + p.h / 2, '#3366cc');
      p.teleport(dest.x, dest.y);
      this.audio.play('teleport');
      const [tx, ty] = this.renderer.cam.targetFor(level, p.cx, p.y, 0);
      this.renderer.cam.follow(tx, ty, 0, true);
      this.burst(p.cx, p.y + p.h / 2, '#3366cc');
      return;
    }
    if (t.flags & F.LINK && t.link) {
      this.state = 'dive';
      this.dive = 0;
      this.audio.play('dive');
      this.particles.burst(p.cx, p.y + p.h, 18, t.flags & F.EXIT ? '#f7c948' : '#3366cc', 160, 'spark', 200);
      void this.travel(t.link, 'link');
    }
  }

  private onPlayerEvent(e: PlayerEvent, t?: Tile): void {
    const p = this.player!;
    switch (e) {
      case 'jump':
        this.audio.play('jump');
        this.particles.dust(p.cx, p.y + p.h, 4);
        break;
      case 'spring':
        this.audio.play('spring');
        this.particles.burst(p.cx, p.y + p.h, 8, '#ef9a1a', 110, 'dot', 300);
        break;
      case 'land':
        this.audio.play('land');
        this.particles.dust(p.cx, p.y + p.h, 7);
        if (t && t.flags & F.ICE) this.particles.burst(p.cx, p.y + p.h, 5, '#9fdcf2', 70);
        break;
      case 'bonk':
        this.audio.play('bonk');
        break;
      case 'hazard':
        if (this.state === 'play') this.hurt(t?.link ? `“${t.link}” doesn't exist yet!` : undefined);
        break;
      default:
        break;
    }
  }

  respawn(): void {
    const p = this.player;
    const level = this.level;
    if (!p || !level || this.state !== 'play') return;
    const s = level.sections[this.checkpoint] ?? level.sections[0];
    p.teleport(s.spawn.x, s.spawn.y);
    this.audio.play('teleport');
  }

  private hurt(reason?: string): void {
    const p = this.player!;
    const level = this.level!;
    if (p.invuln > 0 || this.state !== 'play') return;
    this.deaths++;
    this.audio.play('hurt');
    this.particles.burst(p.cx, p.y + p.h / 2, 22, '#26282c', 190, 'dot', 500);
    this.hurtFlash = 0.35;
    this.renderer.cam.shake = 1;
    const s = level.sections[this.checkpoint] ?? level.sections[0];
    p.teleport(s.spawn.x, s.spawn.y);
    p.invuln = 1.4;
    if (reason) this.hooks.toast(`🥀 Red link: ${reason}`, 'bad');
    this.hooks.hud();
  }

  private coins(): void {
    const p = this.player!;
    const coins = this.level!.coins;
    for (let i = 0; i < coins.length; i++) {
      if (this.coinsTaken[i]) continue;
      const c = coins[i];
      if (Math.abs(c.x - p.cx) < 13 && c.y > p.y - 9 && c.y < p.y + p.h + 7) {
        this.coinsTaken[i] = 1;
        this.sources++;
        this.audio.play('coin');
        this.particles.burst(c.x, c.y, 7, '#f7c948', 90, 'spark', 100);
        this.particles.text(c.x, c.y - 8, '+1', '#9a6a00');
        this.hooks.hud();
      }
    }
  }

  private updateEnemies(dt: number): void {
    const p = this.player!;
    const world = this.world!;
    for (const e of this.enemies) {
      if (Math.abs(e.y - p.y) > 1000) continue;
      e.step(dt, world);
      if (!e.alive) continue;
      if (p.x < e.x + e.w && p.x + p.w > e.x && p.y < e.y + e.h && p.y + p.h > e.y) {
        if (p.vy > 60 && p.y + p.h - e.y < 10) {
          e.alive = false;
          p.vy = -430;
          this.sources += 2;
          this.audio.play('stomp');
          this.particles.burst(e.x + e.w / 2, e.y + e.h / 2, 14, e.kind === 'cn' ? '#6c5ba8' : '#9b2c2c', 150);
          this.particles.text(e.x + e.w / 2, e.y - 10, e.kind === 'cn' ? 'Sourced! +2' : 'Reverted! +2', '#14866d');
          this.hooks.hud();
        } else this.hurt();
      }
    }
  }

  private burst(x: number, y: number, color: string): void {
    this.particles.add({ x, y, vx: 0, vy: 0, life: 0.45, size: 6, color, kind: 'ring' });
    this.particles.burst(x, y, 10, color, 120, 'spark', 0);
  }

  private confetti(): void {
    const p = this.player!;
    const colors = ['#f7c948', '#3366cc', '#d73333', '#14866d', '#795cb2', '#ef9a1a'];
    this.particles.add({
      x: p.cx + (Math.random() - 0.5) * 300,
      y: p.y - 200 - Math.random() * 60,
      vx: (Math.random() - 0.5) * 60,
      vy: 40 + Math.random() * 60,
      life: 2.4,
      size: 2 + Math.random() * 2.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      kind: Math.random() < 0.5 ? 'spark' : 'dot',
      gravity: 60,
    });
  }

  private win(): void {
    this.state = 'won';
    this.wonT = 0;
    this.winPending = false;
    this.audio.play('win');
    const p = this.player!;
    this.particles.text(p.cx, p.y - 40, '★ Found it! ★', '#9a6a00');
  }

  private async finish(): Promise<void> {
    const target = this.target;
    const raceId = this.raceId;
    let shortcut: string[] | undefined;
    if (target && this.startLinks) {
      const start = this.path[0];
      const direct = [...target.aliases].some((a) => this.startLinks!.has(a));
      if (direct && this.hops > 1) shortcut = [start, target.title];
      else if (!direct && this.hops > 2) {
        try {
          const g = await Promise.race([this.targetGraph(), new Promise<never>((_, r) => setTimeout(() => r(new Error()), 2500))]);
          const mids = [...this.startLinks].filter((t) => g.backlinks.has(t)).sort((a, b) => a.length - b.length);
          if (mids.length) shortcut = [start, mids[0], target.title];
        } catch {
          /* no shortcut info */
        }
      }
    }
    if (raceId !== this.raceId) return;
    this.hooks.win({
      cfg: this.cfg!,
      target: target?.title ?? '',
      hops: this.hops,
      timeMs: this.timeMs,
      deaths: this.deaths,
      sources: this.sources,
      insights: this.insights,
      path: [...this.path],
      shortcut,
    });
  }

  private camera(dt: number): void {
    const cam = this.renderer.cam;
    const level = this.level!;
    const p = this.player!;
    if (this.state === 'peek' && this.peek) {
      const [tx, ty] = cam.targetFor(level, this.peek.x, this.peek.y, 0);
      cam.follow(tx, ty, dt * 1.4);
    } else {
      const [tx, ty] = cam.targetFor(level, p.cx, p.y, p.vy);
      cam.follow(tx, ty, dt);
    }
  }

  private actionLabel(): string | null {
    const t = this.actionTile;
    if (!t || this.state !== 'play') return null;
    if (t.door)
      switch (t.door.kind) {
        case 'toc':
          return `↓ jump to “${this.level!.sections[t.door.section]?.title ?? ''}”`;
        case 'contents':
          return '↓ back to contents';
        case 'top':
          return '↓ back to top';
        case 'back':
          return `↓ go back to “${this.stack[this.stack.length - 2] ?? ''}” (+1 hop)`;
      }
    if (!t.link) return null;
    return t.flags & F.EXIT ? `↓ dive: ${t.link} ★` : `↓ ${t.link}`;
  }

  private nearest(list: Occurrence[]): Occurrence | null {
    const p = this.player!;
    let best: Occurrence | null = null;
    let bd = Infinity;
    for (const o of list) {
      const d = Math.abs(o.y - p.y) + Math.abs(o.x - p.x) * 0.3;
      if (d < bd) {
        bd = d;
        best = o;
      }
    }
    return best;
  }

  private indicators(): Indicator[] {
    const out: Indicator[] = [];
    if (this.state !== 'play' && this.state !== 'arrive') return out;
    const exit = this.nearest(this.exitOccs);
    if (exit) out.push({ x: exit.x + 10, y: exit.y + 10, color: '#ffc933', label: `★ ${exit.title}` });
    const hot = this.nearest(this.hotOccs);
    if (hot && !exit) out.push({ x: hot.x + 10, y: hot.y + 10, color: '#ff8a3d', label: `🔥 ${hot.title}` });
    if (this.findOcc !== null) {
      const o = this.level!.occs[this.findOcc];
      if (o) out.push({ x: o.x + 10, y: o.y + 10, color: '#d63384', label: `🔍 ${o.text}` });
    }
    return out;
  }

  private mapMarks(): Indicator[] {
    const out: Indicator[] = [];
    for (const o of this.hotOccs) out.push({ x: o.x, y: o.y, color: '#ff8a3d', label: '' });
    for (const o of this.exitOccs) out.push({ x: o.x, y: o.y, color: '#e8a800', label: '' });
    if (this.findOcc !== null) {
      const o = this.level!.occs[this.findOcc];
      if (o) out.push({ x: o.x, y: o.y, color: '#d63384', label: '' });
    }
    return out;
  }

  private scene(): Scene {
    return {
      level: this.level!,
      world: this.world!,
      player: this.player!,
      enemies: this.enemies,
      coinsTaken: this.coinsTaken,
      particles: this.particles,
      time: this.time,
      checkpoint: this.checkpoint,
      actionTile: this.state === 'play' ? this.actionTile : null,
      actionLabel: this.actionLabel(),
      indicators: this.indicators(),
      dive: this.dive,
      hurtFlash: this.hurtFlash,
      wipe: this.wipe,
      depth: this.hops,
      showPlayer: this.state !== 'travel',
      reducedMotion: this.reducedMotion,
    };
  }
}

const NO_INPUT = { left: false, right: false, up: false, down: false, jump: false, jumpPressed: false };
